import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { PageURLMaster } from "../APIs/APIUrls";
import { GetAllPropertyforWeb } from "../APIs/APIHelperforWeb";

const normalizeToken = (value) =>
  (value || "")
    .toString()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

// Only render on these routes
const allowedRoutes = [
  /^\/property-details\/[^/]+$/,
  /^\/[^/]+$/, // Generic single-segment routes (category pages)
  /^\/contact-us$/,
  /^\/privacy-policy$/,
  /^\/about-us$/,
  /^\/our-services$/,
  /^\/author\/[^/]+$/,
  /^\/blog-grid$/,
  /^\/blog-detail\/[^/]+$/,
];

const Breadcrumb = () => {
  const { pathname } = useLocation();
  const [pageUrlMasterData, setPageUrlMasterData] = useState([]);
  const [propertyData, setPropertyData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if current route is allowed
  const shouldRenderBreadcrumb = allowedRoutes.some((pattern) => pattern.test(pathname));

  // Check if on property-details route and extract title from pathname
  const isPropertyDetailsRoute = pathname.startsWith("/property-details/");
  const propertyTitle = isPropertyDetailsRoute
    ? pathname.replace(/^\/property-details\/+/, "").split(/[?#]/)[0]
    : null;

  useEffect(() => {
    const loadData = async () => {
      try {
        // Fetch PageURLMaster data
        const cachedPageData = localStorage.getItem("PageURLMasterListforWeb");
        let pageData = [];

        if (cachedPageData) {
          pageData = JSON.parse(cachedPageData);
        } else {
          const response = await fetch(`${PageURLMaster}GetAllPageURLMasterforWeb`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({}),
          });

          if (response.ok) {
            const result = await response.json();
            pageData = result?.data || result?.Data || [];
            if (pageData.length > 0) {
              localStorage.setItem("PageURLMasterListforWeb", JSON.stringify(pageData));
            }
          }
        }

        setPageUrlMasterData(pageData);

        // Fetch property data if on property-details route
        if (isPropertyDetailsRoute && propertyTitle) {
          const cachedProperties = localStorage.getItem("AllPropertiesforWeb") || "[]";
          let properties = JSON.parse(cachedProperties);

          if (properties.length === 0) {
            properties = await GetAllPropertyforWeb();
            localStorage.setItem("AllPropertiesforWeb", JSON.stringify(properties));
          }

          const property = properties.find((prop) => prop.AutoGeneratedURL === propertyTitle);
          if (property) {
            setPropertyData(property);
          }
        }
      } catch (error) {
        console.error("Error loading breadcrumb data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (shouldRenderBreadcrumb) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [pathname, propertyTitle, isPropertyDetailsRoute, shouldRenderBreadcrumb]);

  if (!shouldRenderBreadcrumb || pathname === "/404" || loading) {
    return null;
  }

  // Build breadcrumb items
  const itemListElement = [];
  const domainName = "https://askmerealty.in";

  // Position 1: Home
  itemListElement.push({
    "@type": "ListItem",
    position: 1,
    name: "Home",
    item: domainName + "/",
  });

  let position = 2;

  // For property-details route: add parent category then property
  if (isPropertyDetailsRoute && propertyData) {
    // Find matching PageURLMaster by property type, city, state
    const matchedPageUrl = pageUrlMasterData.find((page) => {
      const pageCity = (page?.City || "")
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, "")
        .replace(/\s+/g, "");
      const pageState = (page?.State || "")
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, "")
        .replace(/\s+/g, "");
      const pagePropertyType = (page?.PropertyTypeName || "")
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, "")
        .replace(/\s+/g, "");

      const propCity = (propertyData?.CityName || "")
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, "")
        .replace(/\s+/g, "");
      const propState = (propertyData?.StateName || "")
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, "")
        .replace(/\s+/g, "");
      const propPropertyType = (propertyData?.PropertyTypeName || "")
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, "")
        .replace(/\s+/g, "");

      return (
        pageCity === propCity &&
        pageState === propState &&
        pagePropertyType === propPropertyType
      );
    });

    // Add category page if found
    if (matchedPageUrl?.PageURL) {
      itemListElement.push({
        "@type": "ListItem",
        position,
        name: matchedPageUrl._objDetailPage?.Title || matchedPageUrl.PageName || "Properties",
        item: domainName + "/" + (matchedPageUrl.PageURL || "").replace(/^\/+/, ""),
      });
      position++;
    }

    // Add property details page
    itemListElement.push({
      "@type": "ListItem",
      position,
      name:
        propertyData?.MetaTitle ||
        propertyData?.PropertyTitle ||
        "Property Details",
      item: domainName + "/property-details/" + propertyTitle,
    });
  } else {
    // For non-property-details routes: add the matching PageURLMaster entry
    const normalizedPath = pathname
      .toLowerCase()
      .replace(/^\/+|\/+$/g, "")
      .replace(/\//g, "-");

    const matchedPage = pageUrlMasterData.find((page) => {
      const pageUrl = (page?.PageURL || "")
        .toLowerCase()
        .replace(/^\/+|\/+$/g, "")
        .replace(/\//g, "-");
      return pageUrl === normalizedPath || pageUrl.includes(normalizedPath);
    });

    if (matchedPage?.PageURL) {
      itemListElement.push({
        "@type": "ListItem",
        position,
        name: matchedPage._objDetailPage?.Title || matchedPage.PageName || "Page",
        item: domainName + "/" + (matchedPage.PageURL || "").replace(/^\/+/, ""),
      });
    } else {
      // Fallback: create breadcrumb from pathname
      const pathSegments = pathname
        .split("/")
        .filter(Boolean)
        .map((segment) => segment.replace(/-/g, " ").toUpperCase());

      if (pathSegments.length > 0) {
        itemListElement.push({
          "@type": "ListItem",
          position,
          name: pathSegments[pathSegments.length - 1] || "Page",
          item: domainName + pathname,
        });
      }
    }
  }

  if (itemListElement.length <= 1) {
    return null;
  }

  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement,
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

export default Breadcrumb;
